import React from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import ForHobbyists from './components/ForHobbyists';
import ForDevelopers from './components/ForDevelopers';
import WizantixAI from './components/WizantixAI';
import Showcase from './components/Showcase';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import {
  initFirebaseAnalytics,
  normalizeAnalyticsTarget,
  setAnalyticsConsent,
  trackAnalyticsEvent,
} from './lib/analytics';
import type { Analytics } from 'firebase/analytics';

function App() {
  const [analytics, setAnalytics] = React.useState<Analytics | null>(null);

  const initializeAnalyticsIfAvailable = React.useCallback(async (): Promise<Analytics | null> => {
    const analyticsInstance = await initFirebaseAnalytics();
    if (!analyticsInstance) {
      return null;
    }

    setAnalytics(analyticsInstance);
    trackAnalyticsEvent(analyticsInstance, 'app_loaded', {
      section: 'app',
      action: 'load',
      target: 'landing',
      path: window.location.pathname,
    });

    return analyticsInstance;
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    const initializeAnalytics = async () => {
      const analyticsInstance = await initializeAnalyticsIfAvailable();
      if (!isMounted) {
        return;
      }

      if (analyticsInstance) {
        setAnalytics(analyticsInstance);
      }
    };

    void initializeAnalytics();

    return () => {
      isMounted = false;
    };
  }, [initializeAnalyticsIfAvailable]);

  React.useEffect(() => {
    // Update page title
    document.title = "Wizantix.games - Create Games with AI";
    
    // Set favicon
    const link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (link) {
      link.href = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%236d28d9'><path d='M6 7H18V17H6V7Z' stroke='%236d28d9' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/><path d='M12 17V13' stroke='%236d28d9' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/><path d='M15 9V10' stroke='%236d28d9' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/><path d='M9 9V10' stroke='%236d28d9' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/><path d='M9 13H15' stroke='%236d28d9' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>";
    }
  }, []);

  React.useEffect(() => {
    const handleConsentUpdated = (event: WindowEventMap['cookie-consent-updated']) => {
      const applyConsent = async () => {
      const choice = event.detail.analytics ? 'accepted' : 'declined';

        let analyticsInstance = analytics;

        if (choice === 'accepted' && !analyticsInstance) {
          analyticsInstance = await initializeAnalyticsIfAvailable();
        }

        setAnalyticsConsent(analyticsInstance, choice);

        if (event.detail.source !== 'user') {
          return;
        }

        trackAnalyticsEvent(analyticsInstance, choice === 'accepted' ? 'consent_granted' : 'consent_declined', {
          section: 'cookie_banner',
          action: choice === 'accepted' ? 'accept' : 'decline',
          target: 'analytics',
          path: window.location.pathname,
        });
      };

      void applyConsent();
    };

    window.addEventListener('cookie-consent-updated', handleConsentUpdated);

    return () => {
      window.removeEventListener('cookie-consent-updated', handleConsentUpdated);
    };
  }, [analytics, initializeAnalyticsIfAvailable]);

  React.useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    // Use rAF to ensure layout is complete before scrolling.
    // Avoid smooth scroll — not reliably supported in all WebView environments.
    requestAnimationFrame(() => {
      const id = hash.slice(1); // strip leading '#'
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView();
      }
    });
  }, []);

  React.useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const clickedElement = event.target;
      const explicitlyTrackedContainer = clickedElement?.closest('[data-analytics-explicit="true"]');

      if (explicitlyTrackedContainer) {
        return;
      }

      const button = clickedElement?.closest('button');
      if (!button) {
        return;
      }

      const sectionName = button.closest('section')?.id || 'global';
      const buttonText = button.textContent?.trim() || '';
      const buttonLabel = button.getAttribute('aria-label') || buttonText || 'unknown_button';
      const normalizedButtonTarget = normalizeAnalyticsTarget(buttonLabel, 'unknown_button');
      const buttonType = button.getAttribute('type') || 'button';

      trackAnalyticsEvent(analytics, 'button_click', {
        section: sectionName,
        action: 'click',
        target: normalizedButtonTarget,
        path: window.location.pathname,
        button_type: buttonType,
      });
    };

    const handleDocumentLinkClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const clickedElement = event.target;
      const explicitlyTrackedContainer = clickedElement?.closest('[data-analytics-explicit="true"]');

      if (explicitlyTrackedContainer) {
        return;
      }

      const button = clickedElement?.closest('button');
      if (button) {
        return;
      }

      const anchor = clickedElement?.closest('a');
      if (!anchor) {
        return;
      }

      const sectionName = anchor.closest('section')?.id || 'global';
      const linkText = anchor.textContent?.trim() || '';
      const linkLabel = anchor.getAttribute('aria-label') || linkText || 'unknown_link';
      const normalizedLinkTarget = normalizeAnalyticsTarget(linkLabel, 'unknown_link');
      const href = anchor.getAttribute('href') || '';

      let linkType = 'internal';
      if (href.startsWith('http://') || href.startsWith('https://')) {
        linkType = 'external';
      } else if (href.startsWith('#') || href.includes('#')) {
        linkType = 'hash';
      }

      trackAnalyticsEvent(analytics, 'link_click', {
        section: sectionName,
        action: 'click',
        target: normalizedLinkTarget,
        path: window.location.pathname,
        link_type: linkType,
      });
    };

    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('click', handleDocumentLinkClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
      document.removeEventListener('click', handleDocumentLinkClick);
    };
  }, [analytics]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        <Hero />
        <Features />
        <ForHobbyists />
        <ForDevelopers />
        <WizantixAI />
        <Showcase />
        <Testimonials />
        <Contact />
        <CallToAction />
      </main>
      <Footer />
      <CookieConsent />
    </div>
  );
}

export default App;